import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LegalMention } from '../components/legal';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Divider, Field, MicrosoftButton, PasswordField } from '../components/ui';
import { ApiError, api } from '../services/api';
import { completeHandoff, useHandoff } from '../services/handoff';
import { useSession } from '../session';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { reload } = useSession();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/';
  const handoff = useHandoff();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const tokens = await api.login(email.trim(), password);
      if (handoff) {
        completeHandoff(handoff, tokens); // the add-in's dialog page takes it from here
        return;
      }
      await reload();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(loginError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout title={t('login.title')} subtitle={t('login.subtitle')}>
      {handoff && <Alert kind="info">{t('handoff.notice')}</Alert>}
      {/* A first Microsoft sign-in creates the account (sculpt-api auth/microsoft.py). */}
      <LegalMention />
      <MicrosoftButton href={handoff ? api.microsoftAddinUrl(handoff.origin) : api.microsoftLoginUrl(returnTo)} label={t('auth.microsoft')} />
      <Divider label={t('auth.or')} />
      <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <Field id="email" label={t('auth.email')} type="email" autoComplete="username" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <PasswordField id="password" label={t('auth.password')} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} required />
        {error && <Alert kind="error">{error}</Alert>}
        <Button type="submit" disabled={busy || !email.trim() || !password} className="mt-1 w-full">
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
        <Link to="/forgot" className="text-center text-[12.5px] text-muted underline hover:text-ink">
          {t('auth.forgot')}
        </Link>
      </form>
      <p className="text-center text-[13px] text-muted">
        {t('auth.noAccount')}{' '}
        <Link to="/signup" state={location.state} className="text-accent underline hover:text-accent-dark">
          {t('auth.createAccount')}
        </Link>
      </p>
    </PublicLayout>
  );
}

function loginError(err: unknown, t: (k: string) => string): string {
  if (!(err instanceof ApiError)) return t('app.error');
  if (err.is('rate_limited')) return t('login.rateLimited');
  if (err.status === 401) return err.body.includes('disabled') ? t('auth.accountDisabled') : t('auth.invalidCredentials');
  return t('app.error');
}
